// src/pages/CoreAssure/Kpi/Kpi.jsx
//
// CoreAssure KPI Risk Engine (D-048 §4.3) — Day 5-7 MVP page.
//
// Tenant-wide KPI dashboard across Quality / Operational / Financial-proxy
// categories. 10 KPIs aggregated from existing audit_log event types +
// existing tables. Server-side 5-min in-memory cache; cache MISS writes
// one CA-KPI-VIEW audit_log row.

import React, { useEffect, useState, useCallback } from "react";
import {
  Container, Stack, Typography, Card, CardContent,
  Button, Breadcrumbs, Box, CircularProgress, Alert, Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

import { kpiApi } from "../../../utils/coreassure/kpiApi";
import KpiCategoryGroup from "../../../components/CoreAssure/Kpi/KpiCategoryGroup";

export default function Kpi() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewDisabled, setPreviewDisabled] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ forceRefresh } = {}) => {
    setLoading(true);
    setError(null);
    setPreviewDisabled(false);
    try {
      const res = await kpiApi.fetch({ forceRefresh });
      setData(res?.data?.data || null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setPreviewDisabled(true);
        setData(null);
      } else {
        const msg = err?.response?.data?.error || err.message || "Failed to load KPI dashboard";
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  let cacheStatus = "";
  if (data) {
    cacheStatus = data.fromCache ? "Cached" : "Fresh";
  }

  let computedDisplay = "";
  try {
    if (data && data.computedAt) computedDisplay = new Date(data.computedAt).toLocaleString();
  } catch (_) { computedDisplay = data?.computedAt || ""; }

  return (
    <Container maxWidth="xl">
      <Helmet><title>KPI Risk Engine | SFM</title></Helmet>

      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <Typography color="text.primary">CoreAssure</Typography>
        <Typography color="text.primary">KPI Risk Engine</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">CoreAssure KPI Risk Engine</Typography>
          <Typography variant="body2" color="text.secondary">
            10 KPIs across Quality / Operational / Financial-proxy categories. Aggregated from existing
            audit_log event types + existing tables. 5-min in-memory cache. CoreAssure powered.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {cacheStatus && (
            <Chip
              label={cacheStatus}
              size="small"
              color={data?.fromCache ? "default" : "success"}
              variant="outlined"
            />
          )}
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => load({ forceRefresh: true })}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </Stack>
      </Stack>

      {previewDisabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          KPI Risk Engine preview is not enabled for this tenant. Per D-048 §6 pilot guardrails,
          the per-tenant feature flag <code>coreassure_kpi_enabled</code> is OFF by default.
          Contact your administrator to enable.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {data && Array.isArray(data.categories) && data.categories.map((cat) => (
        <Box key={cat.id} sx={{ mb: 3 }}>
          <KpiCategoryGroup category={cat} />
        </Box>
      ))}

      {data && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="caption" color="text.secondary">
              Tenant: <b>{data.tenantName || `#${data.tenantId}`}</b> · Computed {computedDisplay} · Cache expires {data.cacheExpiresAt ? new Date(data.cacheExpiresAt).toLocaleTimeString() : "—"}
            </Typography>
          </CardContent>
        </Card>
      )}

      {!data && !loading && !previewDisabled && !error && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              No KPI data loaded yet. Click <b>Refresh</b> to fetch.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
